/**
 * Seniors Tier Adoption Analytics
 *
 * Tracks the full conversion funnel for the Seniors tier:
 *   - seniors_walkthrough_started      : User opens the interactive walkthrough
 *   - seniors_walkthrough_step         : User advances to a specific walkthrough step
 *   - seniors_walkthrough_completed    : User reaches the final "Done" step
 *   - seniors_walkthrough_abandoned    : User closes the walkthrough before completing
 *   - seniors_scholar_clicked          : User clicks/selects a scholar card
 *   - seniors_scholar_selected         : User confirms a scholar selection (radio chosen)
 *   - seniors_subscription_cta_clicked : User clicks a subscription CTA button
 *   - seniors_region_payment_clicked   : User clicks a regional payment link
 *   - seniors_signup_from_walkthrough  : User clicks "Create Free Account" from Done step
 */

import { createClient } from '@/lib/supabase/client';

// ─── Region identifiers (6 supported regions) ─────────────────────────────────
export type SeniorsRegion =
  | 'kenya' |'uganda' |'somalia' |'indonesia' |'pakistan' |'malaysia';

// ─── Walkthrough step names ────────────────────────────────────────────────────
export type WalkthroughStepName =
  | 'intro' |'directory' |'scholar-profile' |'ask-flow' |'select-scholar' |'done';

// ─── Event types ──────────────────────────────────────────────────────────────
export type SeniorsEventType =
  | 'seniors_walkthrough_started' |'seniors_walkthrough_step' |'seniors_walkthrough_completed' |'seniors_walkthrough_abandoned' |'seniors_scholar_clicked' |'seniors_scholar_selected' |'seniors_subscription_cta_clicked' |'seniors_region_payment_clicked' |'seniors_signup_from_walkthrough';

// ─── Property interfaces ───────────────────────────────────────────────────────
export interface WalkthroughStartedProperties {
  source: 'tour_button' | 'direct';
}

export interface WalkthroughStepProperties {
  step_name: WalkthroughStepName;
  step_index: number;
  total_steps: number;
}

export interface WalkthroughCompletedProperties {
  total_steps: number;
  time_spent_seconds?: number;
}

export interface WalkthroughAbandonedProperties {
  abandoned_at_step: WalkthroughStepName;
  abandoned_at_index: number;
  total_steps: number;
  completion_rate: number; // 0–1 fraction
}

export interface ScholarClickedProperties {
  scholar_id: string;
  scholar_name: string;
  scholar_specialisations: string[];
  scholar_available: boolean;
  context: 'walkthrough_directory' | 'walkthrough_select' | 'marketing_preview';
}

export interface ScholarSelectedProperties {
  scholar_id: string;
  scholar_name: string;
  scholar_specialisations: string[];
  step: WalkthroughStepName;
}

export interface SubscriptionCtaClickedProperties {
  tier_id: string;
  tier_name: string;
  cta_label: string;
  source: 'pricing_section' | 'walkthrough_done' | 'seniors_section';
}

export interface RegionPaymentClickedProperties {
  region: SeniorsRegion;
  country: string;
  currency: string;
  payment_method: string;
  source: 'pricing_section' | 'seniors_section';
}

export interface SignupFromWalkthroughProperties {
  completed_all_steps: boolean;
  last_step_reached: WalkthroughStepName;
}

type SeniorsEventProperties =
  | WalkthroughStartedProperties
  | WalkthroughStepProperties
  | WalkthroughCompletedProperties
  | WalkthroughAbandonedProperties
  | ScholarClickedProperties
  | ScholarSelectedProperties
  | SubscriptionCtaClickedProperties
  | RegionPaymentClickedProperties
  | SignupFromWalkthroughProperties
  | Record<string, unknown>;

// ─── Core tracking function ────────────────────────────────────────────────────
async function trackSeniorsEvent(
  eventType: SeniorsEventType,
  properties: SeniorsEventProperties,
  userId?: string
): Promise<void> {
  try {
    const supabase = createClient();
    await supabase.from('analytics_events').insert({
      user_id: userId ?? null,
      event_type: eventType,
      event_category: 'seniors_adoption',
      properties,
      subscription_tier: 'seniors',
    });
  } catch (err) {
    console.warn(`[seniorsTracking] Failed to track "${eventType}":`, err);
  }
}

// ─── Public helpers ────────────────────────────────────────────────────────────

/** Track when the interactive walkthrough is opened */
export async function trackWalkthroughStarted(
  props: WalkthroughStartedProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_walkthrough_started', props, userId);
}

/** Track each step the user advances to in the walkthrough */
export async function trackWalkthroughStep(
  props: WalkthroughStepProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_walkthrough_step', props, userId);
}

/** Track when the user reaches the final "Done" step (full completion) */
export async function trackWalkthroughCompleted(
  props: WalkthroughCompletedProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_walkthrough_completed', props, userId);
}

/** Track when the user closes the walkthrough before completing it */
export async function trackWalkthroughAbandoned(
  props: WalkthroughAbandonedProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_walkthrough_abandoned', props, userId);
}

/** Track when a user clicks/views a scholar card */
export async function trackScholarClicked(
  props: ScholarClickedProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_scholar_clicked', props, userId);
}

/** Track when a user confirms a scholar selection (radio button chosen) */
export async function trackScholarSelected(
  props: ScholarSelectedProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_scholar_selected', props, userId);
}

/** Track when a user clicks a subscription CTA button */
export async function trackSubscriptionCtaClicked(
  props: SubscriptionCtaClickedProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_subscription_cta_clicked', props, userId);
}

/** Track when a user clicks a regional payment link (conversion funnel step) */
export async function trackRegionPaymentClicked(
  props: RegionPaymentClickedProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_region_payment_clicked', props, userId);
}

/** Track when a user clicks "Create Free Account" from the walkthrough Done step */
export async function trackSignupFromWalkthrough(
  props: SignupFromWalkthroughProperties,
  userId?: string
): Promise<void> {
  return trackSeniorsEvent('seniors_signup_from_walkthrough', props, userId);
}

// ─── Utility: compute completion rate ─────────────────────────────────────────
export function computeCompletionRate(stepIndex: number, totalSteps: number): number {
  if (totalSteps <= 0) return 0;
  return Math.round((stepIndex / (totalSteps - 1)) * 100) / 100;
}
